import React from 'react'
import {
  List,
  useTable,
  ShowButton,
  EditButton,
  DeleteButton,
  DateField,
  CreateButton,
} from '@refinedev/antd'
import { useList } from '@refinedev/core'
import { Table, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { useRowShow, ListPage, TableSurface, type Stat } from '../../theme/listkit'
import { Image as ImageIcon, BarChart3 } from '../../components/icons'

type Album = Tables<'albums'>
type Photo = Tables<'photos'>

export function AlbumList() {
  const { tableProps } = useTable<Album>({
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'created_at', order: 'desc' }],
    },
  })
  const onRow = useRowShow('albums')

  // Bounded counts for the deck — pageSize:1 queries that read PostgREST's
  // `total` (the count comes back without the rows) instead of pulling the whole
  // `albums` / `photos` tables to the client.
  const { result: albumsRes } = useList<Album>({
    resource: 'albums',
    pagination: { pageSize: 1 },
  })
  // Photo total across all albums (a different resource).
  const { result: photosRes } = useList<Photo>({
    resource: 'photos',
    pagination: { pageSize: 1 },
  })

  const albumCount = albumsRes?.total ?? 0
  const photoCount = photosRes?.total ?? 0

  const stats: Stat[] = [
    { label: 'Total Albums', value: albumCount.toLocaleString(), icon: ImageIcon, tone: 'gold' },
    { label: 'Total Photos', value: photoCount.toLocaleString(), icon: BarChart3, tone: 'jaguar' },
  ]

  return (
    <ListPage
      intro={{
        eyebrow: 'Content · Photos',
        description:
          'Photo albums that organize the gallery — Homecoming, reunions, sports, and campus life. Group memories into collections members can browse and contribute to.',
        actions: <CreateButton />,
      }}
      stats={stats}
      statColumns={2}
    >
      <List title={false} breadcrumb={false}>
        <TableSurface>
          <Table {...tableProps} rowKey="id" scroll={{ x: true }} onRow={onRow}>
            <Table.Column<Album>
              dataIndex="name"
              title="Name"
              render={(name: string) => (
                <span style={{ fontFamily: brand.fDisplay, fontWeight: 700, color: brand.ink }}>{name}</span>
              )}
            />
            <Table.Column<Album>
              dataIndex="created_at"
              title="Created"
              sorter
              render={(value: string) => <DateField value={value} format="MMM D, YYYY" />}
            />
            <Table.Column<Album>
              title="Actions"
              dataIndex="actions"
              render={(_: unknown, record: Album) => (
                <Space>
                  <ShowButton hideText size="small" recordItemId={record.id} />
                  <EditButton hideText size="small" recordItemId={record.id} />
                  <DeleteButton hideText size="small" recordItemId={record.id} />
                </Space>
              )}
            />
          </Table>
        </TableSurface>
      </List>
    </ListPage>
  )
}
